import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CategorySelect from 'common/components/CategorySelect';
import ApexCharts from 'react-apexcharts';

import DashboardCard from 'areas/dashboard/components/DashboardCard';
import { PageSection } from 'common/styles/page';

import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { AggregateType } from 'common/constants/AggregateType';
import {
  getSelectedAssessmentPeriodCategories,
  getSelectedAssessmentPeriod,
  getSelectedAssessmentPeriodId,
} from 'common/selectors/auth';

const AnalyticsOverviewHeat = () => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const assessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const assessmentPeriodCategories = useSelector(getSelectedAssessmentPeriodCategories);
  const assessmentPeriodName = assessmentPeriod?.name;

  const { data: data_by_option } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_PILLARS,
    assessmentPeriodId: assessmentPeriodId,
  });

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [xLabels, setXLabels] = useState<string[]>([]);
  const [yLabels, setYLabels] = useState<string[]>([]);
  const [heatmapSeries, setHeatmapSeries] = useState<
    { name: string; data: { x: string; y: number }[] }[]
  >([]);

  useEffect(() => {
    if (!assessmentPeriodCategories || assessmentPeriodCategories.length === 0) {
      setSelectedCategory(null);
      return;
    }

    const selectedCategoryExists = assessmentPeriodCategories.some(
      (category: any) => category.id === selectedCategory
    );

    if (selectedCategoryExists) return;

    const primaryCategory = assessmentPeriodCategories.find(
      (category: any) => category.priority === 'primary'
    );

    setSelectedCategory(primaryCategory?.id || assessmentPeriodCategories[0]?.id || null);
  }, [assessmentPeriodCategories, selectedCategory]);

  useEffect(() => {
    if (data_by_option && selectedCategory) {
      const filteredOptionData = data_by_option[0]?.result?.pillars.filter(
        (score: { category_id: string }) => score.category_id === selectedCategory
      );

      const xLabels: string[] = Array.from(
        new Set(filteredOptionData.map((score: { pillar_name: string }) => score.pillar_name))
      );
      const yLabels: string[] = Array.from(
        new Set(filteredOptionData.map((score: { option_name: string }) => score.option_name))
      );

      const heatmapSeries: { name: string; data: { x: string; y: number }[] }[] = yLabels.map(
        yLabel => ({
          name: yLabel,
          data: xLabels.map(xLabel => {
            const match = filteredOptionData.find(
              (score: { option_name: string; pillar_name: string }) =>
                score.option_name === yLabel && score.pillar_name === xLabel
            );
            return { x: xLabel, y: match ? parseFloat(match.overall_score.toFixed(1)) : 0 };
          }),
        })
      );

      setXLabels(xLabels);
      setYLabels(yLabels);
      setHeatmapSeries(heatmapSeries);
    }
  }, [data_by_option, selectedCategory]);

  function getColor(value: number) {
    let red = 245;
    let green = 47;
    let blue = 47;

    if (value < 5) {
      return 'rgb(' + Math.floor(red) + ',' + Math.floor(green) + ',' + Math.floor(blue) + ')';
    }
    let remainder = value - 5;
    green = remainder > 2.143 ? 245 : green + remainder * 92.4;
    red = remainder > 4.286 ? 47 : remainder > 2.143 ? red - (remainder - 2.143) * 92.4 : red;
    blue = remainder - 4.286 > 0 ? blue + (remainder - 4.286) * 92.4 : blue;

    return 'rgb(' + Math.floor(red) + ',' + Math.floor(green) + ',' + Math.floor(blue) + ')';
  }

  const chartOptions = {
    legend: {
      show: false,
    },
    tooltip: {
      enabled: false,
      shared: false,
    },

    chart: {
      type: 'heatmap' as const,
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: ['#555'],
        fontSize: '12px',
        fontFamily: 'Poppins',
      },
    },
    xaxis: {
      position: 'top',
      categories: xLabels,
      labels: {
        style: {
          colors: ['#555'],
          fontSize: '12px',
          fontFamily: 'Poppins',
        },
      },
    },
    yaxis: {
      categories: yLabels,
      labels: {
        style: {
          colors: ['#555'],
          fontSize: '12px',
          fontFamily: 'Poppins',
        },
      },
    },
    colors: ['#008FFB'],

    plotOptions: {
      heatmap: {
        colorScale: {
          ranges: Array.from({ length: 40 }, (_, index) => {
            const color = getColor(index / 4);
            return {
              from: index / 4,
              to: index / 4 + 0.25,
              color: color,
            };
          }),
        },
      },
    },
  };

  return (
    <DashboardCard
      title={`Pillar Heat Map: ${assessmentPeriodName}`}
      height="650px"
      controls={
        <CategorySelect
          categories={assessmentPeriodCategories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          placeholder="Select Category"
          width="250px"
        />
      }
    >
      <PageSection
        display="flex"
        flexDirection="column"
        padtop
        style={{ width: '100%', height: '100%' }}
      >
        <ApexCharts
          options={chartOptions}
          series={heatmapSeries}
          type="heatmap"
          height="100%"
          width="100%"
        />
      </PageSection>
    </DashboardCard>
  );
};

export default AnalyticsOverviewHeat;

